import React, { useState, useEffect } from 'react';
import { goalsAPI } from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Skeleton } from '../../components/ui/skeleton';
import { Target, Plus, Edit2, Trash2 } from 'lucide-react';
import { toast } from 'sonner';

export default function AdminOKRs() {
  const [goals, setGoals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState({ title: '', description: '', target: '', current: 0, unit: '%', category: 'editorial', key_results: [] });
  const [krInput, setKrInput] = useState('');

  useEffect(() => { load(); }, []);

  const load = async () => {
    setLoading(true);
    try { const r = await goalsAPI.list(); setGoals(r.data.goals || []); }
    catch { toast.error('Failed'); }
    finally { setLoading(false); }
  };

  const openCreate = () => { setEditId(null); setForm({ title: '', description: '', target: '', current: 0, unit: '%', category: 'editorial', key_results: [] }); setShowDialog(true); };

  const openEdit = (g) => { setEditId(g.id); setForm({ title: g.title, description: g.description || '', target: g.target || '', current: g.current || 0, unit: g.unit || '%', category: g.category || 'editorial', key_results: g.key_results || [] }); setShowDialog(true); };

  const handleSave = async () => {
    if (!form.title.trim()) { toast.error('Title required'); return; }
    try {
      if (editId) { await goalsAPI.update(editId, form); toast.success('Goal updated'); }
      else { await goalsAPI.create(form); toast.success('Goal created'); }
      setShowDialog(false); load();
    } catch { toast.error('Failed to save'); }
  };

  const handleDelete = async (id) => {
    try { await goalsAPI.delete(id); toast.success('Deleted'); load(); }
    catch { toast.error('Failed'); }
  };

  const addKR = () => { if (!krInput.trim()) return; setForm(f => ({ ...f, key_results: [...f.key_results, { text: krInput.trim(), done: false }] })); setKrInput(''); };

  const progress = (g) => {
    const t = parseFloat(g.target) || 100;
    const c = parseFloat(g.current) || 0;
    return Math.min(100, Math.round((c / t) * 100));
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2"><Target className="w-6 h-6 text-[#B89B5E]" /> OKRs & Goals</h1>
          <p className="text-sm text-[#5B616E] mt-1">Objectives and key results for individuals and teams</p>
        </div>
        <Button onClick={openCreate} className="bg-[#121316] text-white hover:bg-[#1B1D22]"><Plus className="w-4 h-4 mr-2" /> New Goal</Button>
      </div>

      {loading ? (
        <div className="space-y-3">{[1,2,3].map(i => <Skeleton key={i} className="h-28" />)}</div>
      ) : goals.length === 0 ? (
        <div className="text-center py-12 text-[#5B616E]"><Target className="w-12 h-12 mx-auto mb-3 opacity-20" /><p>No goals set yet. Create your first OKR.</p></div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {goals.map(g => {
            const pct = progress(g);
            return (
              <Card key={g.id} className="bg-white border-[#E7E0D6]">
                <CardContent className="p-4">
                  <div className="flex items-start justify-between mb-2">
                    <div>
                      <h3 className="font-semibold text-sm">{g.title}</h3>
                      <Badge variant="outline" className="text-[10px] mt-0.5">{g.category}</Badge>
                    </div>
                    <div className="flex gap-1">
                      <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => openEdit(g)}><Edit2 className="w-3.5 h-3.5" /></Button>
                      <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-red-500" onClick={() => handleDelete(g.id)}><Trash2 className="w-3.5 h-3.5" /></Button>
                    </div>
                  </div>
                  <div className="mb-3">
                    <div className="flex justify-between text-xs text-[#5B616E] mb-1">
                      <span>Progress</span>
                      <span>{g.current}{g.unit} / {g.target}{g.unit} ({pct}%)</span>
                    </div>
                    <div className="h-2 bg-[#E7E0D6] rounded-full">
                      <div className="h-2 rounded-full transition-all" style={{ width: `${pct}%`, background: pct >= 100 ? '#1F7A5A' : pct >= 60 ? '#B89B5E' : '#B3261E' }} />
                    </div>
                  </div>
                  {(g.key_results || []).length > 0 && (
                    <div className="space-y-1">
                      {g.key_results.map((kr, i) => (
                        <div key={i} className="flex items-center gap-2 text-xs">
                          <input type="checkbox" checked={!!kr.done} onChange={async e => {
                            const updated = [...g.key_results];
                            updated[i] = { ...kr, done: e.target.checked };
                            await goalsAPI.update(g.id, { key_results: updated });
                            load();
                          }} className="rounded" />
                          <span className={kr.done ? 'line-through text-[#5B616E]' : ''}>{kr.text}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader><DialogTitle>{editId ? 'Edit Goal' : 'New Goal / OKR'}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <Input placeholder="Objective title *" value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} />
            <Input placeholder="Description" value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} />
            <div className="grid grid-cols-3 gap-3">
              <Input type="number" placeholder="Target" value={form.target} onChange={e => setForm(f => ({ ...f, target: e.target.value }))} />
              <Input type="number" placeholder="Current" value={form.current} onChange={e => setForm(f => ({ ...f, current: parseFloat(e.target.value) || 0 }))} />
              <Input placeholder="Unit (%, articles…)" value={form.unit} onChange={e => setForm(f => ({ ...f, unit: e.target.value }))} />
            </div>
            <Select value={form.category} onValueChange={v => setForm(f => ({ ...f, category: v }))}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>{['editorial', 'audience', 'revenue', 'technical', 'hr', 'personal'].map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
            </Select>
            <div>
              <label className="text-xs font-medium block mb-1">Key Results</label>
              {form.key_results.map((kr, i) => (
                <div key={i} className="flex gap-1 mb-1">
                  <span className="text-xs flex-1 px-2 py-1 bg-[#FBF7EF] rounded">{kr.text}</span>
                  <Button size="sm" variant="ghost" className="h-6 text-red-500 p-1" onClick={() => setForm(f => ({ ...f, key_results: f.key_results.filter((_, j) => j !== i) }))}><Trash2 className="w-3 h-3" /></Button>
                </div>
              ))}
              <div className="flex gap-1">
                <Input value={krInput} onChange={e => setKrInput(e.target.value)} placeholder="Add key result..." className="text-xs" onKeyDown={e => e.key === 'Enter' && (e.preventDefault(), addKR())} />
                <Button size="sm" variant="outline" className="text-xs" onClick={addKR}>+</Button>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowDialog(false)}>Cancel</Button>
            <Button onClick={handleSave} className="bg-[#121316] text-white hover:bg-[#1B1D22]">{editId ? 'Update' : 'Create'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
